/**
 * The stripped prologue's chamber — issue #22, docs/08-castlevania-dev-lessons §2.
 *
 * The SotN commentary's best idea: you begin overpowered, in a short authored
 * sequence, and are then stripped of everything. It teaches the systems while
 * you still have the toys to teach them with, and it is what makes the real
 * run's poverty *mean* something rather than just be the starting state.
 *
 * THIS FILE IS THE SPACE, NOT THE SCRIPT. It owns geometry and named marks and
 * nothing else: no state machine, no ability grants, no HUD. `src/ui/prologue.ts`
 * owns what the player reads and `src/core/game.ts` owns the sequence, and the
 * three meet at one thing — the beat ids. Every `PrologueMark.id` below is a
 * member of `PROLOGUE_BEATS`, and `tests/prologue.test.ts` asserts the two lists
 * are the same set, in the same order, so a beat can never be authored with
 * nowhere to happen (or a room built for a beat that was cut).
 *
 * WHY IT IS HAND-BUILT. Everything else in `world/` is a pure function of a
 * seed, and the whole point of this one is that it is not. #22's acceptance
 * criteria are about *teaching* — movement, one traversal ability, one weapon,
 * one material interaction — and a lesson you might or might not be given is
 * not a lesson. It is still a `ChunkGenerator`, so it goes through the same
 * `CellWorld` as the run and shares its eviction, simulation and rendering; it
 * simply ignores `(cx, cy)`'s usual meaning and reads a rect list instead.
 *
 * WHY IT IS ONE CORRIDOR, WEST TO EAST. The surface (`worldgen.ts` `grove`)
 * already teaches direction the way Noita's opening does — west is scenery, east
 * is the hole. The prologue is the same sentence spoken once before the run, so
 * it reads left to right and ends by handing you east.
 *
 * THE GEOMETRY IS SIZED AGAINST `movementTuning.ts`, not against taste:
 * `jumpHeight` 54, `jumpApexTime` 0.34 s, run 240 cells/s, dash 430 × 0.145 s ≈
 * 62 cells. `PROLOGUE_GAP_W` is comfortably inside a standing jump and
 * `PROLOGUE_DASH_GAP_W` is deliberately outside one from the run-up it is given
 * — `tests/prologue.test.ts` drives the real mover across both rather than
 * trusting that arithmetic.
 */

import { Flag, VOID } from '../sim/materials';
import { MAT, registerMaterials } from '../sim/materialData';
import { CHUNK_SHIFT, CHUNK_SIZE, type Chunk, type ChunkGenerator } from '../sim/world';

// ------------------------------------------------------------------ the shape

/** Floor level of the opening run. The player's feet rest at `y = -1`. */
export const PROLOGUE_FLOOR = 0;
/**
 * Floor level after the dash gap.
 *
 * Six cells *below* the opening run, not above it. The obvious staging is a
 * step up, so the far lip reads as somewhere you have to reach — and measured
 * with the real mover it makes the gap uncrossable rather than hard, because a
 * dash is horizontal (`dashDirY` is zero with a neutral stick) and adds nothing
 * to the arc's height. A step down keeps the lesson about reach, which is the
 * thing the dash actually solves.
 */
export const PROLOGUE_FLOOR_2 = 6;
/** Ceiling of the corridor. 84 cells is three bodies: a hall, not a tube. */
export const PROLOGUE_CEIL = -84;

/** West and east walls. Outside this, solid rock forever. */
export const PROLOGUE_X0 = 8;
export const PROLOGUE_X1 = 1120;

/** The whole authored volume, as a clipping rect. */
export const PROLOGUE_BOUNDS = { x0: -64, y0: -160, x1: PROLOGUE_X1 + 64, y1: 128 };

/** Where the player wakes. On the floor, clear of everything. */
export const PROLOGUE_SPAWN = { x: 48, y: PROLOGUE_FLOOR - 12 };

/**
 * The two gaps.
 *
 * Both measured with the real mover in `tests/prologue.test.ts`, not derived.
 * 32 is inside a standing jump and well inside a running one. 136 is not: from
 * the 80-cell run-up in front of it a plain running jump lands at x≈419, in the
 * pit, on the catwalk that lets you climb back out and try again. One tap of
 * Shift at the apex adds `dashSpeed × dashDuration` ≈ 62 cells and lands at
 * x≈458, two cells past the lip.
 *
 * The beat script (`ui/prologue.ts`) prompts `Space` at the first and `Shift` at
 * the second, and the difference between them is the entire lesson.
 */
export const PROLOGUE_GAP_W = 32;
export const PROLOGUE_DASH_GAP_W = 136;

/** West edge of each gap. Exported so a test can stand on the far lip. */
export const PROLOGUE_GAP_X = 168;
export const PROLOGUE_DASH_X = 320;
const GAP_X = PROLOGUE_GAP_X;
const DASH_X = PROLOGUE_DASH_X;

/** The oil basin. Struck with a lit blade, it teaches docs/03 §7 in one frame. */
export const PROLOGUE_SLICK = { x: 540, y: PROLOGUE_FLOOR_2, w: 84, h: 22 };

/**
 * The breach seam — a wall across the corridor with no way around it.
 *
 * `RUSTED_IRON` on purpose: hardness 3, brittleness 0.7, "one hit from powder".
 * The prologue's lesson is that terrain is a material with properties, not a
 * boundary, and the material that says so most clearly is the one that says it
 * in its own description.
 */
export const PROLOGUE_SEAM = { x: 700, y: PROLOGUE_CEIL, w: 14, h: -PROLOGUE_CEIL + PROLOGUE_FLOOR_2 };

/** Top of the weapon pedestal. Same shape as a sanctum's altar plinth. */
export const PROLOGUE_PLINTH = { x: 860, y: PROLOGUE_FLOOR_2 - 3 };

/**
 * The sealed room at the end, and the only `ADAMANT_SEAL` in the prologue.
 *
 * The material is the whole staging of the beat. Everything else the player has
 * met in the last ninety seconds gave way to them — the oil burned, the seam
 * broke — and then there is a room made of the one thing in the game that does
 * not. `abilities.ts` `blocksTranslocation` already refuses Blink and Phase
 * Drift through it, so a player who has been handed every traversal ability
 * still cannot leave, and no scripted invulnerability is needed to make that
 * true.
 */
export const PROLOGUE_VAULT = { x0: 1000, x1: PROLOGUE_X1, ceil: -80, floor: PROLOGUE_FLOOR_2 };

/** Where the run begins. The integrator moves the player to the world surface. */
export const PROLOGUE_EXIT = { x: 1104, y: PROLOGUE_FLOOR_2 - 12 };

/**
 * Ambient light inside the chamber — issue #31.
 *
 * Brighter than any band, and not `DAYLIGHT`: this is indoors, but it is also
 * the first thing a new profile ever sees, and a player who cannot read the
 * room cannot learn anything in it. The glowcap seams along the ceiling do the
 * rest, which is also how the prologue introduces the light-as-biome-identity
 * idea before the descent depends on it.
 */
export const PROLOGUE_AMBIENT = 0.78;

// ------------------------------------------------------------------ the marks

export interface PrologueMark {
  /** Matches a `PrologueBeat.id` in `src/ui/prologue.ts`. */
  id: string;
  /** Trigger centre, in world cells. */
  x: number;
  y: number;
  /** Trigger half-extents. Generous vertically: the player may be mid-jump. */
  halfW: number;
  halfH: number;
}

/**
 * Where each beat happens, west to east.
 *
 * In the order the corridor presents them, which is the order `PROLOGUE_BEATS`
 * declares them — the two lists are one list split across two files because one
 * of them is geometry and the other is words, and a test keeps them honest.
 *
 * `surface` is the terminus and deliberately sits *outside* the chamber's floor
 * plan, at the vault's east wall: it is the beat where the integrator hands the
 * player the standard run kit and moves them to `WorldGen.spawnPoint()`, so its
 * mark is the place that transition fires, not a room.
 */
export const PROLOGUE_MARKS: readonly PrologueMark[] = [
  { id: 'wake', x: PROLOGUE_SPAWN.x, y: PROLOGUE_FLOOR - 12, halfW: 40, halfH: 40 },
  { id: 'gap', x: GAP_X - 20, y: PROLOGUE_FLOOR - 12, halfW: 24, halfH: 40 },
  { id: 'shelf', x: DASH_X - 28, y: PROLOGUE_FLOOR - 12, halfW: 30, halfH: 40 },
  { id: 'slick', x: PROLOGUE_SLICK.x - 22, y: PROLOGUE_FLOOR_2 - 12, halfW: 26, halfH: 40 },
  { id: 'seam', x: PROLOGUE_SEAM.x - 26, y: PROLOGUE_FLOOR_2 - 12, halfW: 30, halfH: 40 },
  { id: 'plinth', x: PROLOGUE_PLINTH.x, y: PROLOGUE_FLOOR_2 - 12, halfW: 28, halfH: 40 },
  { id: 'strip', x: 1050, y: PROLOGUE_FLOOR_2 - 12, halfW: 36, halfH: 40 },
  { id: 'surface', x: PROLOGUE_EXIT.x, y: PROLOGUE_EXIT.y, halfW: 14, halfH: 40 },
];

/** The mark the player is standing in, or null. One poll per frame, eight rects. */
export function prologueMarkAt(x: number, y: number): PrologueMark | null {
  for (const m of PROLOGUE_MARKS) {
    if (x < m.x - m.halfW || x > m.x + m.halfW) continue;
    if (y < m.y - m.halfH || y > m.y + m.halfH) continue;
    return m;
  }
  return null;
}

export function prologueMark(id: string): PrologueMark | undefined {
  return PROLOGUE_MARKS.find((m) => m.id === id);
}

// -------------------------------------------------------------- the generator

interface Op {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Material to write. `VOID` carves. */
  m: number;
}

let ops: Op[] | null = null;

/**
 * The rect list, built once.
 *
 * Read top to bottom as a build order: carve the corridor out of solid rock,
 * step the floor up after the dash gap, then add the features. Later rects win,
 * which is why the seam and the vault come after the corridor that runs through
 * where they are.
 */
function buildOps(): Op[] {
  if (ops !== null) return ops;
  registerMaterials();
  const M = MAT;
  const f1 = PROLOGUE_FLOOR;
  const f2 = PROLOGUE_FLOOR_2;
  const ceil = PROLOGUE_CEIL;
  const out: Op[] = [];

  // 1. The corridor. Two floor levels, with the step at the dash gap so the
  //    far lip is visibly higher than the near one — the gap has to *read* as
  //    something you need help with before the prompt appears.
  out.push({ x: PROLOGUE_X0, y: ceil, w: DASH_X - PROLOGUE_X0, h: f1 - ceil, m: VOID });
  out.push({
    x: DASH_X, y: ceil, w: PROLOGUE_X1 - DASH_X, h: f2 - ceil, m: VOID,
  });

  // 2. The two gaps, cut through the floor into the rock below. Deep enough
  //    that falling in is a real mistake and shallow enough that it is not a
  //    death — the prologue takes nothing from you until the vault does.
  out.push({ x: GAP_X, y: f1, w: PROLOGUE_GAP_W, h: 44, m: VOID });
  out.push({ x: DASH_X, y: f2, w: PROLOGUE_DASH_GAP_W, h: 60, m: VOID });

  // 3. A ledge back out of each pit, one-way so it is a climb and not a wall.
  out.push({ x: GAP_X, y: f1 + 40, w: PROLOGUE_GAP_W, h: 3, m: M.CATWALK });
  out.push({ x: DASH_X + 12, y: f2 + 34, w: 60, h: 3, m: M.CATWALK });
  out.push({ x: DASH_X + 60, y: f2 + 12, w: 60, h: 3, m: M.CATWALK });

  // 4. The oil basin. Cut into the slab, not poured on it, so it is still
  //    there after the player has walked through it — the same reason the
  //    sanctum's healing pool is a basin (`worldgen.ts` `carveSanctum`).
  out.push({
    x: PROLOGUE_SLICK.x, y: PROLOGUE_SLICK.y, w: PROLOGUE_SLICK.w, h: PROLOGUE_SLICK.h, m: VOID,
  });
  out.push({
    x: PROLOGUE_SLICK.x, y: PROLOGUE_SLICK.y + 8, w: PROLOGUE_SLICK.w, h: PROLOGUE_SLICK.h - 8,
    m: M.OIL,
  });

  // 5. The seam. Floor to ceiling, so there is no route over or under it.
  out.push({
    x: PROLOGUE_SEAM.x, y: PROLOGUE_SEAM.y, w: PROLOGUE_SEAM.w, h: PROLOGUE_SEAM.h,
    m: M.RUSTED_IRON,
  });

  // 6. The pedestal.
  out.push({ x: PROLOGUE_PLINTH.x - 5, y: PROLOGUE_PLINTH.y, w: 11, h: 3, m: M.RUNESTONE });

  // 7. The vault: solid seal, then the room hollowed out of it, then the way
  //    in. Closed by construction rather than by four walls whose corners are
  //    left to arithmetic — the same construction `carveSanctum` uses.
  const v = PROLOGUE_VAULT;
  out.push({
    x: v.x0, y: v.ceil - 8, w: v.x1 - v.x0 + 8, h: v.floor - v.ceil + 16, m: M.ADAMANT_SEAL,
  });
  out.push({ x: v.x0 + 8, y: v.ceil, w: v.x1 - v.x0 - 8, h: v.floor - v.ceil, m: VOID });
  out.push({ x: v.x0, y: f2 - 30, w: 10, h: 30, m: VOID });

  // 8. Light. Glowcap along the ceiling at a 32-cell pitch, which is the same
  //    span `worldgen.ts` `glowVeins` uses, so the prologue's rooms are lit by
  //    the same rule the world's are (#31).
  for (let x = 40; x < PROLOGUE_X1 - 16; x += 32) {
    out.push({ x, y: ceil - 3, w: 10, h: 3, m: M.GLOWCAP });
  }
  for (let x = v.x0 + 16; x < v.x1 - 12; x += 32) {
    out.push({ x, y: v.ceil - 3, w: 8, h: 3, m: M.GLOWCAP });
  }

  ops = out;
  return out;
}

/**
 * A `ChunkGenerator` for the prologue chamber.
 *
 * Solid granite everywhere, with the rect list carved and dressed into it. Every
 * placed cell is `STATIC`: the prologue is authored, and a corridor that has
 * slumped into a different shape by the time the player reaches it is not.
 * `OIL` is the one exception — it is meant to flow, and the whole beat is that
 * it does.
 */
export function prologueGenerator(): ChunkGenerator {
  return (chunk: Chunk, cx: number, cy: number) => {
    const baseX = cx << CHUNK_SHIFT;
    const baseY = cy << CHUNK_SHIFT;
    const mat = chunk.mat;
    const temp = chunk.temp;
    const flags = chunk.flags;
    const aux = chunk.aux;

    mat.fill(MAT.GRANITE);
    temp.fill(20);
    flags.fill(Flag.STATIC);
    aux.fill(0);

    for (const op of buildOps()) {
      const x0 = Math.max(0, op.x - baseX);
      const x1 = Math.min(CHUNK_SIZE - 1, op.x + op.w - 1 - baseX);
      const y0 = Math.max(0, op.y - baseY);
      const y1 = Math.min(CHUNK_SIZE - 1, op.y + op.h - 1 - baseY);
      if (x1 < x0 || y1 < y0) continue;
      const isOil = op.m === MAT.OIL;
      for (let ly = y0; ly <= y1; ly++) {
        const row = ly << CHUNK_SHIFT;
        for (let lx = x0; lx <= x1; lx++) {
          const i = row + lx;
          mat[i] = op.m;
          flags[i] = op.m === VOID || isOil ? 0 : Flag.STATIC;
        }
      }
    }
  };
}

/** Test-only: drop the cached rect list. */
export function _resetPrologue(): void {
  ops = null;
}
